import React from "react";
import { connect } from "react-redux";
import swal from "sweetalert";
import Select from "../../components/shared/form/ReactSelect";
import Page from "../../components/shared/pages/Page";
import Loading from "../../components/shared/loaders/Loading";
import store from "../../Store";
import { setDashboardMenu } from "../../routes/main-route/MainRoute-Actions";
import {
  fetchPlans,
  updatePlan,
  upsertPrice,
  removePrice,
  isNotDeployed,
  unwrapList,
} from "./billingApi";

const CURRENCIES = ["SEK", "USD", "EUR", "GBP", "NOK", "DKK"];
const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({ value: c, label: c }));
const PLAN_STATUS_OPTIONS = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive (hidden from customers)" },
];

// Screen 1 — Plans & Stripe pricing. Manages the plan <-> Stripe mapping
// (product id + one price id per currency). Stripe objects themselves are
// created in the Stripe dashboard; this only stores the mapping.
class BillingPlans extends React.Component {
  state = {
    loading: true,
    notDeployed: false,
    error: "",
    plans: [],
    editingPlan: null, // plan id
    editForm: { title: "", stripe_product_id: "", status: "active" },
    addingPriceFor: null, // plan id
    priceForm: { currency: "SEK", stripe_price_id: "" },
    saving: false,
  };

  componentDidMount() {
    document.querySelector("body").classList.add("organization-dashboard");
    store.dispatch(setDashboardMenu(""));
    this.load();
  }

  componentWillUnmount() {
    document.querySelector("body").classList.remove("organization-dashboard");
  }

  load = () => {
    this.setState({ loading: true, error: "", notDeployed: false });
    fetchPlans()
      .then((res) => this.setState({ plans: unwrapList(res), loading: false }))
      .catch((err) => {
        if (isNotDeployed(err)) {
          this.setState({ notDeployed: true, loading: false });
        } else {
          this.setState({
            error: "Could not load plans. Check the API and try again.",
            loading: false,
          });
        }
      });
  };

  // union of currencies configured on any plan = currencies the platform serves
  servedCurrencies() {
    const set = new Set();
    this.state.plans.forEach((p) =>
      (p.prices || []).forEach((pr) =>
        set.add((pr.currency || "").toUpperCase()),
      ),
    );
    return [...set].filter(Boolean);
  }

  missingCurrencies(plan) {
    const own = new Set(
      (plan.prices || []).map((pr) => (pr.currency || "").toUpperCase()),
    );
    return this.servedCurrencies().filter((c) => !own.has(c));
  }

  startEdit = (plan) => {
    this.setState({
      editingPlan: plan.id,
      addingPriceFor: null,
      editForm: {
        title: plan.title || "",
        stripe_product_id: plan.stripe_product_id || "",
        status:
          plan.status === true || plan.status === "active"
            ? "active"
            : "inactive",
      },
    });
  };

  savePlan = (plan) => {
    const { editForm } = this.state;
    if (
      editForm.stripe_product_id &&
      !editForm.stripe_product_id.startsWith("prod_")
    ) {
      swal(
        "Check the Product ID",
        "Stripe product ids start with prod_",
        "warning",
      );
      return;
    }
    this.setState({ saving: true });
    updatePlan(plan.id, { ...editForm, status: editForm.status === "active" })
      .then(() => {
        this.setState({ editingPlan: null, saving: false });
        this.load();
      })
      .catch(() => {
        this.setState({ saving: false });
        swal("Save failed", "The plan could not be updated.", "error");
      });
  };

  savePrice = (plan) => {
    const { priceForm } = this.state;
    if (!priceForm.stripe_price_id.startsWith("price_")) {
      swal(
        "Check the Price ID",
        "Stripe price ids start with price_",
        "warning",
      );
      return;
    }
    this.setState({ saving: true });
    upsertPrice(plan.id, {
      currency: priceForm.currency,
      stripe_price_id: priceForm.stripe_price_id,
    })
      .then(() => {
        this.setState({
          addingPriceFor: null,
          priceForm: { currency: "SEK", stripe_price_id: "" },
          saving: false,
        });
        this.load();
      })
      .catch(() => {
        this.setState({ saving: false });
        swal("Save failed", "The price mapping could not be saved.", "error");
      });
  };

  deletePrice = (plan, currency) => {
    swal({
      title: `Remove ${currency} price for ${plan.title}?`,
      text: "Customers in that currency will no longer be able to subscribe to this plan.",
      icon: "warning",
      buttons: ["Cancel", "Remove"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      removePrice(plan.id, currency)
        .then(this.load)
        .catch(() => swal("Remove failed", "", "error"));
    });
  };

  render() {
    const { role } = this.props;
    const {
      loading,
      notDeployed,
      error,
      plans,
      editingPlan,
      editForm,
      addingPriceFor,
      priceForm,
      saving,
    } = this.state;
    const canWrite = role === "super-admin";
    const served = this.servedCurrencies();
    const plansMissing = plans.filter((p) => this.missingCurrencies(p).length);

    return (
      <Page title="Plans & Stripe">
        <div className="heading-block pr-60">
          <div className="title-block">
            <h2>Plans &amp; Stripe pricing</h2>
            <p className="billing-sub">
              Maps each plan to its Stripe product and one price per currency.
              Prices are created in the Stripe dashboard and pasted here — the
              Stripe Price is always the authoritative amount.
            </p>
          </div>
        </div>

        {notDeployed ? (
          <div className="billing-notice">
            The billing endpoints are not deployed yet (portal-api&nbsp;·{" "}
            <code>stripe-ground-zero</code>). This screen will populate once{" "}
            <code>GET /admin/billing/plans</code> is live.
          </div>
        ) : error ? (
          <div className="billing-notice billing-notice--error">{error}</div>
        ) : null}

        {plansMissing.length > 0 && (
          <div className="billing-notice billing-notice--warn">
            {plansMissing
              .map(
                (p) =>
                  `${p.title || p.slug}: missing ${this.missingCurrencies(p).join(", ")}`,
              )
              .join(" · ")}{" "}
            — customers in a missing currency cannot subscribe (create-site
            returns 422).
          </div>
        )}

        <div className="table-block table-responsive pr-60">
          <table className="table table-striped  ">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Slug</th>
                <th>Product ID</th>
                <th>Prices (per currency)</th>
                <th>Status</th>
                {canWrite && <th className="text-center">Action</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    <Loading />
                  </td>
                </tr>
              ) : plans.length ? (
                plans.map((plan) => (
                  <React.Fragment key={plan.id}>
                    <tr>
                      <td>
                        {plan.title}
                        {plan.price ? (
                          <div className="billing-muted">
                            display price {plan.price} (informational)
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <code>{plan.slug}</code>
                      </td>
                      <td>
                        <code>{plan.stripe_product_id || "—"}</code>
                      </td>
                      <td>
                        {(plan.prices || []).map((pr) => (
                          <span className="price-chip" key={pr.currency}>
                            <strong>{(pr.currency || "").toUpperCase()}</strong>
                            <code>{pr.stripe_price_id}</code>
                            {pr.amount ? (
                              <span className="billing-muted">{pr.amount}</span>
                            ) : null}
                            {canWrite && (
                              <button
                                className="price-chip__x"
                                title="Remove this currency price"
                                onClick={() =>
                                  this.deletePrice(
                                    plan,
                                    (pr.currency || "").toUpperCase(),
                                  )
                                }
                              >
                                ×
                              </button>
                            )}
                          </span>
                        ))}
                        {this.missingCurrencies(plan).map((c) => (
                          <span
                            className="price-chip price-chip--missing"
                            key={c}
                          >
                            {c} missing
                          </span>
                        ))}
                        {canWrite && (
                          <button
                            className="billing-link-btn"
                            onClick={() =>
                              this.setState({
                                addingPriceFor:
                                  addingPriceFor === plan.id ? null : plan.id,
                                editingPlan: null,
                              })
                            }
                          >
                            + Add price
                          </button>
                        )}
                      </td>
                      <td>
                        <span
                          className={`billing-badge ${
                            plan.status === true || plan.status === "active"
                              ? "ok"
                              : "off"
                          }`}
                        >
                          {plan.status === true || plan.status === "active"
                            ? "active"
                            : "inactive"}
                        </span>
                      </td>
                      {canWrite && (
                        <td className="text-center">
                          <button
                            className="billing-link-btn"
                            onClick={() =>
                              editingPlan === plan.id
                                ? this.setState({ editingPlan: null })
                                : this.startEdit(plan)
                            }
                          >
                            {editingPlan === plan.id ? "Cancel" : "Edit"}
                          </button>
                        </td>
                      )}
                    </tr>

                    {editingPlan === plan.id && (
                      <tr className="billing-form-row">
                        <td colSpan={6}>
                          <div className="billing-inline-form">
                            <label>
                              Title
                              <input
                                className="form-control"
                                value={editForm.title}
                                onChange={(e) =>
                                  this.setState({
                                    editForm: {
                                      ...editForm,
                                      title: e.target.value,
                                    },
                                  })
                                }
                              />
                            </label>
                            <label>
                              Stripe product id
                              <input
                                className="form-control"
                                placeholder="prod_…"
                                value={editForm.stripe_product_id}
                                onChange={(e) =>
                                  this.setState({
                                    editForm: {
                                      ...editForm,
                                      stripe_product_id: e.target.value.trim(),
                                    },
                                  })
                                }
                              />
                            </label>
                            <label>
                              Status
                              <Select
                                value={
                                  PLAN_STATUS_OPTIONS.find(
                                    (o) => o.value === editForm.status
                                  ) || null
                                }
                                options={PLAN_STATUS_OPTIONS}
                                onChange={(selected) =>
                                  this.setState({
                                    editForm: {
                                      ...editForm,
                                      status: selected ? selected.value : "",
                                    },
                                  })
                                }
                                noFormGroup
                              />
                            </label>
                            <button
                              className="btn-theme btn-dark-blue"
                              disabled={saving}
                              onClick={() => this.savePlan(plan)}
                            >
                              {saving ? "Saving…" : "Save plan"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {addingPriceFor === plan.id && (
                      <tr className="billing-form-row">
                        <td colSpan={6}>
                          <div className="billing-inline-form">
                            <label>
                              Currency
                              <Select
                                value={
                                  CURRENCY_OPTIONS.find(
                                    (o) => o.value === priceForm.currency
                                  ) || null
                                }
                                options={CURRENCY_OPTIONS}
                                onChange={(selected) =>
                                  this.setState({
                                    priceForm: {
                                      ...priceForm,
                                      currency: selected ? selected.value : "",
                                    },
                                  })
                                }
                                noFormGroup
                              />
                            </label>
                            <label>
                              Stripe price id
                              <input
                                className="form-control"
                                placeholder="price_…"
                                value={priceForm.stripe_price_id}
                                onChange={(e) =>
                                  this.setState({
                                    priceForm: {
                                      ...priceForm,
                                      stripe_price_id: e.target.value.trim(),
                                    },
                                  })
                                }
                              />
                            </label>
                            <button
                              className="btn-theme btn-dark-blue"
                              disabled={saving}
                              onClick={() => this.savePrice(plan)}
                            >
                              {saving ? "Saving…" : "Save price"}
                            </button>
                            <span className="billing-muted">
                              Existing {priceForm.currency} mapping for this
                              plan is overwritten. The moment all plans have a
                              price in a currency, customers in it can be billed
                              — no deploy needed.
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : !notDeployed ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    No plans configured
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Page>
    );
  }
}

function mapStateToProps(state) {
  let { route } = state;
  let role = route && route.data.user && route.data.user.role.slug;
  return { role };
}

export default connect(mapStateToProps)(BillingPlans);
